import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import { TITLE_TEXT, TITLE_BUTTONS } from './titleConfig';
import { playBgm, playSe } from '../utils/audioManager';
import { AudioKey } from '../constants/audioKeys';
import { BackgroundImageKey } from '../constants/imageKeys';
import { fitImage } from '../utils/imageUtils';
import { GameMode } from '../types/GameMode';
import { loadSurvivalSave } from '../utils/survivalManager';
import { getSurvivalAiLevel, getSurvivalEnemyStage } from '../constants/survivalConfig';
import { getMonsterWithGrownStats } from '../constants/monsterStats';
import { MONSTER_DATABASE } from '../constants/monsters';
import { Monster } from '../types/Monster';
import { UPDATE_INFO } from '../constants/updateInfo';

/**
 * タイトル画面シーン
 *
 * ゲームタイトルとモード選択ボタンを表示する。
 */
export class TitleScene extends BaseScene {
  constructor() {
    super(SceneKey.TITLE);
  }

  create(): void {
    this.cameras.main.fadeIn(500, 0, 0, 0);

    if (this.textures.exists(BackgroundImageKey.TITLE)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.TITLE).setOrigin(0.5);
    }
    this.createTitle();
    this.createButtons();
    this.createVersionText();
    this.createUpdateInfoButton();
    playBgm(this.sound, AudioKey.BGM_TITLE);

    if (process.env.NODE_ENV !== 'production') {
      this.setupDebugAccess();
    }
  }

  private createTitle(): void {
    if (this.textures.exists(BackgroundImageKey.TITLE_LOGO)) {
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT * 0.22, BackgroundImageKey.TITLE_LOGO).setOrigin(0.5);
      fitImage(img, GAME_WIDTH * 0.90, GAME_HEIGHT * 0.42);
    } else {
      this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.2, TITLE_TEXT, {
          fontSize: '48px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
    }
  }

  private createVersionText(): void {
    this.add
      .text(
        GAME_WIDTH - 8,
        GAME_HEIGHT - 8,
        `v${__APP_VERSION__}  ${__BUILD_DATE__}`,
        {
          fontSize: '12px',
          color: '#888888',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
        }
      )
      .setOrigin(1, 1);
  }

  private createUpdateInfoButton(): void {
    const btn = this.add
      .text(8, GAME_HEIGHT - 8, '更新情報', {
        fontSize: '16px',
        color: '#888888',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0, 1)
      .setInteractive({ useHandCursor: true });

    btn.on('pointerover', () => btn.setColor('#cccccc'));
    btn.on('pointerout', () => btn.setColor('#888888'));
    btn.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      this.showUpdateInfoOverlay();
    });
  }

  private showUpdateInfoOverlay(): void {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const panelH = 340;
    const panelW = 380;
    const textLeft = cx - panelW / 2 + 10;
    const textWidth = panelW - 20;

    const overlay = this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.75).setDepth(10);
    const panel = this.add.rectangle(cx, cy, panelW, panelH, 0x111122).setStrokeStyle(1, 0x6666aa).setDepth(10);

    const entries = UPDATE_INFO.entries;
    let pageIndex = 0;

    // ページコンテンツ（入れ替えるオブジェクト）
    let pageObjects: Phaser.GameObjects.GameObject[] = [];

    const contentTop = cy - panelH / 2 + 44;
    const contentBottom = cy + panelH / 2 - 44;

    const renderPage = () => {
      pageObjects.forEach((o) => o.destroy());
      pageObjects = [];

      const entry = entries[pageIndex];

      // ページヘッダー（← v1.0.5 (1/3) →）
      const prevBtn = this.add
        .text(cx - 80, cy - panelH / 2 + 26, '←', {
          fontSize: '18px',
          color: pageIndex < entries.length - 1 ? '#aaddff' : '#444466',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
        })
        .setOrigin(0.5)
        .setDepth(10);
      if (pageIndex < entries.length - 1) {
        prevBtn.setInteractive({ useHandCursor: true }).on('pointerdown', () => {
          playSe(this.sound, AudioKey.SE_SELECT);
          pageIndex++;
          renderPage();
        });
      }
      pageObjects.push(prevBtn);

      const pageLabel = this.add
        .text(cx, cy - panelH / 2 + 26, `v${entry.version}  (${pageIndex + 1}/${entries.length})`, {
          fontSize: '14px',
          color: '#ffdd88',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(10);
      pageObjects.push(pageLabel);

      const nextBtn = this.add
        .text(cx + 80, cy - panelH / 2 + 26, '→', {
          fontSize: '18px',
          color: pageIndex > 0 ? '#aaddff' : '#444466',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
        })
        .setOrigin(0.5)
        .setDepth(10);
      if (pageIndex > 0) {
        nextBtn.setInteractive({ useHandCursor: true }).on('pointerdown', () => {
          playSe(this.sound, AudioKey.SE_SELECT);
          pageIndex--;
          renderPage();
        });
      }
      pageObjects.push(nextBtn);

      // 本文
      const body = entry.lines.join('\n');
      const bodyText = this.add
        .text(textLeft, contentTop, body, {
          fontSize: '13px',
          color: '#cccccc',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          lineSpacing: 6,
          wordWrap: { width: textWidth },
        })
        .setOrigin(0, 0)
        .setDepth(10);
      pageObjects.push(bodyText);
    };

    renderPage();

    const titleText = this.add
      .text(cx, cy - panelH / 2 + 10, '更新情報', {
        fontSize: '14px',
        color: '#aaddff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(10);

    const closeBtn = this.add
      .text(cx, contentBottom + 18, '閉じる', {
        fontSize: '20px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(10);

    const destroy = () => {
      pageObjects.forEach((o) => o.destroy());
      [overlay, panel, titleText, closeBtn].forEach((o) => o.destroy());
    };

    closeBtn.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      destroy();
    });
  }

  private createButtons(): void {
    const startY = GAME_HEIGHT * 0.55;
    const spacing = 52;

    TITLE_BUTTONS.forEach((buttonConfig, index) => {
      const y = startY + index * spacing;

      const text = this.add
        .text(GAME_WIDTH / 2, y, buttonConfig.label, {
          fontSize: '28px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on('pointerover', () => {
        text.setColor('#ffffff');
        text.setScale(1.1);
      });

      text.on('pointerout', () => {
        text.setColor('#ffffff');
        text.setScale(1.0);
      });

      text.on('pointerdown', () => {
        playSe(this.sound, AudioKey.SE_SELECT);
        if (buttonConfig.mode === GameMode.SURVIVAL) {
          this.handleSurvivalButton();
        } else {
          this.transitionTo(buttonConfig.targetScene, {
            mode: buttonConfig.mode,
          });
        }
      });
    });
  }

  private handleSurvivalButton(): void {
    const save = loadSurvivalSave();
    if (!save) {
      this.transitionTo(SceneKey.CHARACTER_SELECT, { mode: GameMode.SURVIVAL });
      return;
    }

    // セーブデータがある場合はダイアログで選択
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const overlay = this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7);
    const panel = this.add.rectangle(cx, cy, 420, 200, 0x222222).setStrokeStyle(2, 0x888888);

    this.add
      .text(cx, cy - 60, `続きから再開しますか？（${save.winCount}連勝中）`, {
        fontSize: '18px',
        color: '#ffffff',
        wordWrap: { width: 380 },
        align: 'center',
      })
      .setOrigin(0.5);

    const resumeBtn = this.add
      .text(cx - 90, cy + 30, '続きから', {
        fontSize: '20px',
        color: '#ffffff',
        backgroundColor: '#224422',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    resumeBtn.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      const enemyId = save.opponentQueue[0];
      const enemyStage = getSurvivalEnemyStage(save.winCount);
      const aiLevel = getSurvivalAiLevel(save.winCount);
      const baseMonster = MONSTER_DATABASE.find((m) => m.id === save.monsterId);
      if (!baseMonster) return;
      const playerMonster: Monster = {
        ...baseMonster,
        stats: { ...save.stats, specialAttackCount: Math.floor(save.stats.special / 50) },
      };
      const enemy = getMonsterWithGrownStats(enemyId, enemyStage);
      if (!enemy) return;
      this.transitionTo(SceneKey.SURVIVAL_EXP_ALLOCATION, { saveData: save });
    });

    const newBtn = this.add
      .text(cx + 90, cy + 30, '新しく始める', {
        fontSize: '20px',
        color: '#ffffff',
        backgroundColor: '#442222',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    newBtn.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      overlay.destroy();
      panel.destroy();
      this.transitionTo(SceneKey.CHARACTER_SELECT, { mode: GameMode.SURVIVAL });
    });
  }

  private setupDebugAccess(): void {
    const gh = this.cameras.main.height;
    let holdTimer: ReturnType<typeof setTimeout> | null = null;

    // タイトル上部エリア（画面上40%）の長押し3秒でデバッグ画面へ
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y > gh * 0.4) return;
      holdTimer = setTimeout(() => {
        this.scene.start(SceneKey.DEBUG);
      }, 3000);
    });
    this.input.on('pointerup', () => {
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
    });
    this.input.on('pointerupoutside', () => {
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
    });
  }
}
